// CheckoutScreen.tsx
import React, { useContext, useMemo, useState } from "react";
import "./index.scss";
import NavBar from "../../components/Navbarr";
import Footerr from "../../components/Footerr";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { useSelector, useStore } from "react-redux";
import { calculateTotal } from "../../utils";
import { addOrder } from "../../services/post-order";
import { UserContext } from "../../components/context/userContext";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Store } from "@reduxjs/toolkit";
import Modal from "react-modal";
import ModalViderBasket from "../../components/ModalViderBasket";
import { api } from "../../config/baseApi";

const FREE_SHIPPING_THRESHOLD = 250;
const SHIPPING_FEE = 4;

const fmt = (n: number) => (Number.isFinite(n) ? n : 0).toFixed(3);

const unitPrice = (item: any) =>
  item?.sold > 0 ? (item.price * (100 - item.sold)) / 100 : item?.price ?? 0;

const customStyles = {
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.55)",
    zIndex: 1000,
  },
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    maxWidth: "420px",
    width: "90%",
    padding: 0,
    border: "none",
    borderRadius: 16,
    background: "#fff",
    overflow: "hidden",
  },
};

const validationSchema = Yup.object({
  firstName: Yup.string().trim().required("Le prénom est requis"),
  lastName: Yup.string().trim().required("Le nom est requis"),
  email: Yup.string().email("Adresse e-mail invalide").required("L'e-mail est requis"),
  phone: Yup.string()
    .matches(/^[24579][0-9]{7}$/, "Numéro de téléphone invalide (8 chiffres)")
    .required("Le téléphone est requis"),
  address: Yup.string().trim().required("L'adresse est requise"),
  city: Yup.string().trim().required("La ville est requise"),
  zip: Yup.string()
    .matches(/^[0-9]{4}$/, "Code postal invalide (4 chiffres)")
    .required("Le code postal est requis"),
  country: Yup.string().trim().required("Le pays est requis"),
  note: Yup.string().max(500, "500 caractères maximum"),
});

type CheckoutField = {
  name: string;
  label: string;
  placeholder: string;
  type?: string;
  as?: "textarea";
  full?: boolean;
};

const FIELDS: CheckoutField[] = [
  { name: "firstName", label: "Prénom", placeholder: "Prénom" },
  { name: "lastName", label: "Nom", placeholder: "Nom" },
  { name: "email", label: "E-mail", placeholder: "vous@exemple.com", type: "email" },
  { name: "phone", label: "Téléphone", placeholder: "Ex. 20123456", type: "tel" },
  { name: "address", label: "Adresse", placeholder: "Rue, immeuble, appartement", full: true },
  { name: "city", label: "Ville", placeholder: "Ville" },
  { name: "zip", label: "Code postal", placeholder: "1000", type: "text" },
  { name: "country", label: "Pays", placeholder: "Pays" },
  { name: "note", label: "Note (facultatif)", placeholder: "Instructions de livraison…", as: "textarea", full: true },
];

const PAYMENT_METHODS = [
  { id: "cod", label: "Paiement à la livraison", hint: "Payez en espèces à la réception", disabled: false },
  { id: "card", label: "Carte bancaire", hint: "Bientôt disponible", disabled: true },
];

const CheckoutScreen = () => {
  const store = useStore() as Store;
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [vals, setVals] = useState<any>({});
  const [paymentMethod, setPaymentMethod] = useState("cod");

  const productsInBasket = useSelector(
    (state: Storage) => state?.entities?.basketSlice?.product
  );

  const items: any[] = Array.isArray(productsInBasket) ? productsInBasket : [];
  const isEmpty = items.length === 0;

  const initialValues = useMemo(
    () => ({
      firstName: "",
      lastName: "",
      email: user?.email ?? "",
      phone: "",
      address: "",
      city: "",
      zip: "",
      country: "Tunisie",
      note: "",
    }),
    [user?.email]
  );

  const subTotal = calculateTotal(items) || 0;
  const shipping = isEmpty || subTotal >= FREE_SHIPPING_THRESHOLD ? 0 : SHIPPING_FEE;
  const grandTotal = subTotal + shipping;

  const closeModal = () => setIsOpen(false);

  const handleOpenModal = (values: any) => {
    setVals(values);
    setIsOpen(true);
  };

  async function ViderPanierAction() {
    try {
      store.dispatch({ type: "basketSlice/delete", payload: {} });
      return true;
    } catch (error) {
      return false;
    }
  }

  const handleSubmit = async () => {
    const orderProducts = items.map((item) => ({
      product: item._id,
      name: item.name,
      price: unitPrice(item),
      count: item.quantityInBasket,
    }));

    const data = {
      products: orderProducts,
      amount: grandTotal,
      subTotal,
      deliveryFee: shipping,
      paymentMethod,
      customer: {
        firstName: vals?.firstName,
        lastName: vals?.lastName,
        email: vals?.email,
      },
      note: vals?.note,
      deliveryAddress: {
        zip: vals?.zip,
        country: vals?.country,
        address: vals?.address,
        city: vals?.city,
      },
      phoneNumber: vals?.phone,
      status: "Not processed",
      user: user?._id,
    };

    setLoading(true);
    addOrder(data, user?._id)
      .then((response) => {
        setLoading(false);
        if (response.status == 500) {
          toast.error("Erreur interne du serveur");
          return;
        }
        if (!response?.success) {
          toast.error(response?.message);
          return;
        }
        toast.success("Commande passée avec succès !");
        setIsOpen(false);
        ViderPanierAction().then((ok) => {
          if (ok) navigate("/acceuil");
          else toast.error("Problème lors de la réinitialisation du panier !");
        });
      })
      .catch(() => {
        setLoading(false);
        toast.error("Une erreur est survenue, réessayez.");
      });
  };

  return (
    <div className="checkout-page">
      <NavBar />

      <div className="checkout-shell">
        <header className="checkout-head">
          <h1>Finaliser la commande</h1>
          <p>Renseignez vos informations de livraison pour confirmer votre commande.</p>
        </header>

        <div className="checkout-grid">
          {/* ---------- LEFT : shipping form ---------- */}
          <section className="checkout-card shipping-details">
            <h2 className="card-title">Détails de livraison</h2>

            <Formik
              initialValues={initialValues}
              enableReinitialize
              validationSchema={validationSchema}
              onSubmit={handleOpenModal}
            >
              {() => (
                <Form className="checkout-form" noValidate>
                  <div className="fields-grid">
                    {FIELDS.map((f) => (
                      <div
                        key={f.name}
                        className={`field ${f.full ? "field--full" : ""}`}
                      >
                        <label htmlFor={f.name}>{f.label}</label>
                        {f.as === "textarea" ? (
                          <Field
                            as="textarea"
                            id={f.name}
                            name={f.name}
                            rows={3}
                            placeholder={f.placeholder}
                          />
                        ) : (
                          <Field
                            id={f.name}
                            name={f.name}
                            type={f.type || "text"}
                            placeholder={f.placeholder}
                          />
                        )}
                        <ErrorMessage name={f.name} component="span" className="error" />
                      </div>
                    ))}
                  </div>

                  <fieldset className="payment-methods">
                    <legend>Mode de paiement</legend>
                    {PAYMENT_METHODS.map((m) => (
                      <label
                        key={m.id}
                        className={`pay-option ${
                          paymentMethod === m.id ? "is-selected" : ""
                        } ${m.disabled ? "is-disabled" : ""}`}
                      >
                        <input
                          type="radio"
                          name="paymentMethod"
                          value={m.id}
                          checked={paymentMethod === m.id}
                          disabled={m.disabled}
                          onChange={() => setPaymentMethod(m.id)}
                        />
                        <span className="pay-option__body">
                          <span className="pay-option__label">{m.label}</span>
                          <span className="pay-option__hint">{m.hint}</span>
                        </span>
                      </label>
                    ))}
                  </fieldset>

                  <button
                    type="submit"
                    className="btn-primary btn-place-order"
                    disabled={loading || isEmpty}
                  >
                    {loading
                      ? "Traitement…"
                      : `Confirmer la commande · ${fmt(grandTotal)} DT`}
                  </button>
                  {isEmpty && (
                    <p className="form-hint">
                      Votre panier est vide.{" "}
                      <button
                        type="button"
                        className="link-btn"
                        onClick={() => navigate("/shop")}
                      >
                        Continuer les achats
                      </button>
                    </p>
                  )}
                </Form>
              )}
            </Formik>
          </section>

          {/* ---------- RIGHT : order summary ---------- */}
          <aside className="checkout-card order-summary">
            <h2 className="card-title">Résumé de la commande</h2>

            {isEmpty ? (
              <div className="summary-empty">
                <i className="bx bx-cart" />
                <p>Aucun article dans votre panier.</p>
                <button className="btn-primary" onClick={() => navigate("/shop")}>
                  Voir la boutique
                </button>
              </div>
            ) : (
              <>
                <ul className="summary-lines">
                  {items.map((prod: any, index: number) => (
                    <li className="summary-line" key={prod?._id ?? index}>
                      <span className="summary-line__thumb">
                        <img
                          src={`${api}/${prod?.type === "package" ? "package" : "product"}/photo/${prod?._id}`}
                          alt={prod?.name}
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).style.visibility =
                              "hidden";
                          }}
                        />
                        <b className="summary-line__qty">{prod?.quantityInBasket}</b>
                      </span>
                      <span className="summary-line__name">{prod?.name}</span>
                      <span className="summary-line__price">
                        {fmt(unitPrice(prod) * (prod?.quantityInBasket || 1))} DT
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="summary-totals">
                  <div className="summary-row">
                    <span>Sous-total</span>
                    <span>{fmt(subTotal)} DT</span>
                  </div>
                  <div className="summary-row">
                    <span>Livraison</span>
                    <span>{shipping === 0 ? "Offerte" : `${fmt(shipping)} DT`}</span>
                  </div>
                  {shipping > 0 && (
                    <p className="summary-note">
                      Plus que {fmt(FREE_SHIPPING_THRESHOLD - subTotal)} DT pour la
                      livraison offerte.
                    </p>
                  )}
                  <div className="summary-row summary-row--total">
                    <span>Total</span>
                    <span>{fmt(grandTotal)} DT</span>
                  </div>
                </div>

                <div className="summary-reassurance">
                  <span><i className="bx bx-shield-quarter" /> Paiement sécurisé</span>
                  <span><i className="bx bx-package" /> Livraison 24–72h</span>
                </div>
              </>
            )}
          </aside>
        </div>
      </div>

      <Footerr />

      <Modal
        isOpen={isOpen}
        onRequestClose={closeModal}
        style={customStyles}
        contentLabel="Confirmer la commande"
        ariaHideApp={false}
      >
        <ModalViderBasket
          text={"Êtes-vous sûr de vouloir passer la commande ?"}
          closeModal={closeModal}
          action={handleSubmit}
          loading={loading}
        />
      </Modal>
    </div>
  );
};

export default CheckoutScreen;
